import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Report run list item
export interface ReportRun {
  run_id: string;
  user_id: string;
  user_email: string | null;
  exam_family_id: string;
  modality: string | null;
  status: string;
  created_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  error_code: string | null;
  error_message: string | null;
  token_count: number | null;
}

// Finding unit (extracted text chunk)
export interface FindingUnit {
  finding_unit_id: string;
  source_text: string;
  section_token: string | null;
  order_index: number;
}

// Observation atom (clinical fact)
export interface ObservationAtom {
  observation_id: string;
  structure_key: string;
  attribute_key: string;
  value: string;
  confidence: number | null;
}

// Validation event
export interface ValidationEvent {
  event_id: string;
  event_type: string;
  message: string;
  severity: string;
  created_at: string;
}

// Full report run detail
export interface ReportRunDetail {
  run: ReportRun;
  finding_units: FindingUnit[];
  observation_atoms: ObservationAtom[];
  validation_events: ValidationEvent[];
}

interface ReportRunFilters {
  startDate?: Date;
  endDate?: Date;
  status?: string;
  modality?: string;
  userId?: string;
}

interface UseReportRunsResult {
  runs: ReportRun[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  fetch: (filters?: ReportRunFilters, page?: number) => Promise<void>;
}

interface UseReportRunDetailResult {
  detail: ReportRunDetail | null;
  loading: boolean;
  fetch: (runId: string) => Promise<void>;
  clear: () => void;
}

/**
 * Hook for fetching report run list
 *
 * Note: This attempts to use the admin_list_report_runs RPC. If it doesn't exist,
 * falls back to direct table access on reporting_run.
 */
export function useReportRuns(): UseReportRunsResult {
  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (filters?: ReportRunFilters, pageNum: number = 1) => {
    setLoading(true);
    try {
      // Try RPC first
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_list_report_runs', {
        p_start_date: filters?.startDate?.toISOString() || null,
        p_end_date: filters?.endDate?.toISOString() || null,
        p_status: filters?.status || null,
        p_modality: filters?.modality || null,
        p_user_id: filters?.userId || null,
        p_limit: pageSize,
        p_offset: (pageNum - 1) * pageSize
      });

      if (rpcError) {
        // If RPC doesn't exist, fall back to direct table access
        if (rpcError.code === '42883' || rpcError.code === 'PGRST202' || (rpcError as { status?: number }).status === 404) {
          // RPC not found - fall back to direct table access (silent)

          // Build query - note: table uses 'id' not 'run_id'
          let query = supabase
            .from('reporting_run')
            .select(`
              id,
              user_id,
              exam_family_id,
              modality,
              status,
              created_at,
              completed_at,
              duration_ms,
              error_code,
              error_message
            `, { count: 'exact' })
            .order('created_at', { ascending: false })
            .range((pageNum - 1) * pageSize, pageNum * pageSize - 1);

          // Apply filters
          if (filters?.startDate) {
            query = query.gte('created_at', filters.startDate.toISOString());
          }
          if (filters?.endDate) {
            query = query.lte('created_at', filters.endDate.toISOString());
          }
          if (filters?.status) {
            query = query.eq('status', filters.status);
          }
          if (filters?.userId) {
            query = query.eq('user_id', filters.userId);
          }
          if (filters?.modality) {
            query = query.eq('modality', filters.modality);
          }

          const { data: tableData, error: tableError, count } = await query;

          if (tableError) {
            // Handle missing table, schema mismatch, or RLS errors gracefully
            const errStr = JSON.stringify(tableError);
            const isTableOrSchemaIssue =
              tableError.code === '42P01' ||
              tableError.code === 'PGRST204' ||
              tableError.code === '42703' ||
              tableError.message?.includes('does not exist') ||
              (tableError as { status?: number }).status === 400 ||
              (tableError as { status?: number }).status === 404 ||
              errStr.includes('400') ||
              errStr.includes('404');

            if (isTableOrSchemaIssue) {
              // Silent - return empty data
              setRuns([]);
              setTotal(0);
              setPage(pageNum);
              return;
            }
            throw tableError;
          }

          // Map data - rename 'id' to 'run_id' for interface compatibility
          const mappedRuns = (tableData || []).map((run: { id: string; user_id: string; exam_family_id: string | null; modality: string | null; status: string; created_at: string; completed_at: string | null; duration_ms: number | null; error_code: string | null; error_message: string | null }) => ({
            run_id: run.id,
            user_id: run.user_id,
            user_email: null,
            exam_family_id: run.exam_family_id || '',
            modality: run.modality,
            status: run.status,
            created_at: run.created_at,
            completed_at: run.completed_at,
            duration_ms: run.duration_ms,
            error_code: run.error_code,
            error_message: run.error_message,
            token_count: null
          })) as ReportRun[];

          setRuns(mappedRuns);
          setTotal(count || 0);
          setPage(pageNum);
          return;
        }
        throw rpcError;
      }

      // Parse RPC result
      const result = rpcData as unknown as {
        success: boolean;
        runs: ReportRun[];
        total_count: number;
        error?: string;
      };

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch report runs');
      }

      setRuns(result.runs || []);
      setTotal(result.total_count || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch report runs:', err);
      toast.error('Failed to load report runs');
      setRuns([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  return {
    runs,
    total,
    page,
    pageSize,
    loading,
    fetch
  };
}

/**
 * Hook for fetching full report run detail
 */
export function useReportRunDetail(): UseReportRunDetailResult {
  const [detail, setDetail] = useState<ReportRunDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (runId: string) => {
    setLoading(true);
    try {
      // Try RPC first
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_get_report_run_details', {
        p_run_id: runId
      });

      if (rpcError) {
        // If RPC doesn't exist, build from multiple table queries
        if (rpcError.code === '42883' || rpcError.code === 'PGRST202' || (rpcError as { status?: number }).status === 404) {
          console.warn('admin_get_report_run_details RPC not found - falling back to direct table access');

          // Fetch run info - table uses 'id' not 'run_id'
          const { data: runData, error: runError } = await supabase
            .from('reporting_run')
            .select('*')
            .eq('id', runId)
            .single();

          if (runError) {
            if (runError.code === '42P01' || runError.message?.includes('does not exist') || runError.code === 'PGRST204') {
              console.warn('reporting_run table not found');
              setDetail(null);
              return;
            }
            throw runError;
          }

          // Fetch finding units - may not exist
          let findingsData = null;
          try {
            const { data } = await supabase
              .from('finding_unit')
              .select('*')
              .eq('reporting_run_id', runId)
              .order('position_index');
            findingsData = data;
          } catch {
            console.warn('finding_unit table not accessible');
          }

          // Fetch observation atoms - may not exist
          let atomsData = null;
          try {
            const { data } = await supabase
              .from('observation_atom')
              .select('*')
              .eq('reporting_run_id', runId);
            atomsData = data;
          } catch {
            console.warn('observation_atom table not accessible');
          }

          // Fetch validation events - may not exist
          let eventsData = null;
          try {
            const { data } = await supabase
              .from('extraction_scope_event')
              .select('*')
              .eq('reporting_run_id', runId)
              .order('created_at');
            eventsData = data;
          } catch {
            console.warn('extraction_scope_event table not accessible');
          }

          setDetail({
            run: {
              run_id: runData.id,
              user_id: runData.user_id,
              user_email: null,
              exam_family_id: runData.exam_family_id,
              modality: runData.modality,
              status: runData.status,
              created_at: runData.created_at,
              completed_at: runData.completed_at,
              duration_ms: runData.duration_ms,
              error_code: runData.error_code,
              error_message: runData.error_message,
              token_count: null
            },
            finding_units: (findingsData || []).map((f: { id: string; text: string; section_token: string; position_index: number }) => ({
              finding_unit_id: f.id,
              source_text: f.text || '',
              section_token: f.section_token,
              order_index: f.position_index
            })),
            observation_atoms: (atomsData || []).map((a: { id: string; observation_id: string | null; content_text: string; extraction_confidence: number | null }) => ({
              observation_id: a.observation_id || a.id,
              structure_key: '',
              attribute_key: '',
              value: a.content_text || '',
              confidence: a.extraction_confidence
            })),
            validation_events: (eventsData || []).map((e: { id: string; event_type: string; action_taken: string | null; created_at: string }) => ({
              event_id: e.id,
              event_type: e.event_type,
              message: e.action_taken || '',
              severity: 'info',
              created_at: e.created_at
            }))
          });
          return;
        }
        throw rpcError;
      }

      // Parse RPC result
      const result = rpcData as unknown as {
        success: boolean;
        data: ReportRunDetail;
        error?: string;
      };

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch report run details');
      }

      setDetail(result.data);
    } catch (err) {
      console.error('Failed to fetch report run detail:', err);
      toast.error('Failed to load report details');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setDetail(null);
  }, []);

  return {
    detail,
    loading,
    fetch,
    clear
  };
}
